import axios from "src/lib/axios";

/** --------------------- AI Generate image API --------------------- */

/**
 * @notice This API is for generating image based on prompt and reference images. It will return a new image that is generated by AI.
 * @param {object} data
 * @param {string} data.prompt - prompt text
 * @param {string} data.model - AI model name
 * @param {Base64URLString} data.image1Base64 - base64 string without prefix data:image/...;base64,
 * @param {string} data.image1Type - mime type
 * @param {Base64URLString} data.image2Base64 - base64 string without prefix data:image/...;base64,
 * @param {string} data.image2Type - mime type
 * @returns {Promise<object>} - response data
 */
export const generateContentImage = async ({
  prompt,
  model = "gemini-3-pro-image-preview",
  image1Base64,
  image1Type,
  image2Base64,
  image2Type,
  itemCode = null,
}) => {
  const { data: response } = await axios.post(`/nano-banana-base64`, {
    prompt,
    model,
    image1Base64,
    image1Type,
    image2Base64,
    image2Type,
    itemCode,
  });
  return response.data;
};

export const findAllSuggestArea = async () => {
  const { data: response } = await axios.get(`/suggest-area`);
  return response.data;
};

/** --------------------- AI Generate product detail API --------------------- */

/**
 * @notice This API is for generating product details based on product information and context. It will return a new product detail generated by AI.
 * @param {object} data
 * @param {string} data.productName - product name
 * @param {string} data.images - product images (array of base64 string without prefix data:image/...;base64,)
 * @param {object} data.context - additional context for generating product detail ex. category, productTypes, color, patterns, etc.
 *
 * @return {Promise<IDta>} - response data
 */
export const generateBasicContent = async ({
  productName,
  images = [],
  context,
}) => {
  const { data: response } = await axios.post(`/generate-basic-fields`, {
    productName: productName,
    images: images,
    context: context,
  });
  return response.data;
};

/**
 * @notice genarate field suggestions based on product information and context. It will return a list of suggestions for each field that is generated by AI.
 * @param {object} data
 * @param {string} data.fieldName - the name of the field that you want to generate suggestions for. ex. itemName, hashtags, description, etc.
 * @param {string} data.currentValue - current value of the field // ?
 * @param {string} data.productName - product name
 * @param {string} data.images - product images (array of base64 string without prefix data:image/...;base64,)
 * @param {object} data.context - additional context for generating product detail ex. category, productTypes, color, patterns, etc.
 *
 * @return {Promise<{suggestions: string[]}>} - response data
 */
export const generateFieldSuggestions = async ({
  fieldName,
  currentValue = "",
  productName = "",
  images = [],
  context,
}) => {
  const { data: response } = await axios.post(`/field-suggestions`, {
    fieldName: fieldName,
    currentValue: currentValue,
    productName: productName,
    images: images,
    context: context,
  });
  return response.data;
};

/**
 * @notice genarate hashtags based on product information and context. It will return a list of hashtags generated by AI.
 * @param {object} data
 * @param {string} data.productName - product name
 * @param {string} data.images - product images (array of base64 string without prefix data:image/...;base64,)
 * @param {object} data.context - additional context for generating product detail ex. category, productTypes, color, patterns, etc.
 *
 * @return {Promise<{hashtags: string[]}>} - response data
 */
export const generateHashtagSuggestions = async ({
  productName = "",
  images = [],
  context,
}) => {
  const { data: response } = await axios.post(`/generate-hashtags`, {
    productName: productName,
    images: images,
    context: context,
  });
  return response.data;
};
